import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { favoritesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { EventCard } from '../components/EventCard';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Heart, Loader2, Search, ArrowLeft } from 'lucide-react';

export const Favorites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const res = await favoritesAPI.getAll();
      setFavorites(res.data);
    } catch (error) {
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchFavorites();
  }, [user, navigate, fetchFavorites]);

  if (!user) return null;

  return (
    <div className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dashboard
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight text-[#1E3A5F] mb-2 flex items-center gap-3">
            <Heart className="w-10 h-10 text-[#FE5F55]" />
            Saved Events
          </h1>
          <p className="text-lg text-[#4A4A4A]">
            Your collection of vendor opportunities
          </p>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="card-dashboard text-center py-16">
            <Heart className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
            <h3 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">
              No saved events yet
            </h3>
            <p className="text-[#4A4A4A] mb-6">
              Browse events and click the heart icon to save them here
            </p>
            <Link to="/events">
              <Button className="btn-primary" data-testid="browse-events-btn">
                <Search className="w-4 h-4 mr-2" />
                Browse Events
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-4 text-[#4A4A4A]">
              {favorites.length} saved event{favorites.length !== 1 ? 's' : ''}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {favorites.map((event) => (
                <EventCard key={event.id} event={event} onFavoriteChange={fetchFavorites} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Favorites;
